import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import authService from './services/authService';
import { UserProvider } from './context/UserContext';
import Navbar from './components/Navbar';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';
import { ROUTE_CONFIG, ROUTES } from './constants/routes';
import ROLES from './constants/roles';
import './style/App.css';

// Lazy load page components
const Login = lazy(() => import('./pages/Auth/Login'));
const AIKlassroomDashboard = lazy(() => import('./pages/Content/Dashboard'));
const AudioDashboard = lazy(() => import('./pages/Content/ContentCreator'));
const ContentMaker = lazy(() => import('./pages/Content/ContentMaker'));
const AdminPage = lazy(() => import('./pages/Admin/AdminPage'));
const ActivityTableUpdate = lazy(() => import('./pages/Content/ActivityTableUpdate'));
const ActivityTable = lazy(() => import('./pages/Content/ActivityTable'));
const SchoolTestStudent = lazy(() => import('./pages/Content/SchoolTestStudent'));
const StudentChanceManagement = lazy(() => import('./pages/Content/StudentChanceManagement'));
const ImageTester = lazy(() => import('./pages/Content/ImageTester'));
const TesterDashboard = lazy(() => import('./pages/Content/TesterDashboard'));
const ActivityPromptManager = lazy(() => import('./pages/Admin/ActivityPromptManager'));
const UserManagement = lazy(() => import('./pages/Admin/UserManagement'));
const SchoolManagement = lazy(() => import('./pages/Admin/SchoolManagement'));
const Unauthorized = lazy(() => import('./pages/Shared/Unauthorized'));
const AccessCode = lazy(() => import('./pages/Admin/AccessCode'));
const SchoolTest = lazy(() => import('./pages/Content/SchoolTest'));
const MobileSectionPage = lazy(() => import('./pages/Content/MobileSectionPage'));
const SchoolSectionPage = lazy(() => import('./pages/Content/SchoolSectionPage'));
const ProjectorActivitiesPage = lazy(() => import('./pages/Content/ProjectorActivitiesPage'));
const GraphActivityPage = lazy(() => import('./pages/Content/GraphActivityPage'));
const OfflineActivityPage = lazy(() => import('./pages/Content/OfflineActivityPage'));
const ActivitiesPage = lazy(() => import('./pages/Content/ActivitiesPage'));
const TeacherActivityAssignmentPage = lazy(() => import('./pages/Content/TeacherActivityAssignmentPage'));

// Map component names to lazy loaded components
const COMPONENT_MAP = {
  Login,
  AIKlassroomDashboard,
  AudioDashboard,
  ContentMaker: SchoolSectionPage, // Unified school section page
  AdminPage,
  ActivityTableUpdate: SchoolSectionPage, // Unified school section page
  ActivityTable: SchoolSectionPage, // Unified school section page
  MobileTables: MobileSectionPage, // Unified mobile section page
  MobileActivityTables: MobileSectionPage, // Unified mobile section page
  MobileActivityTableUpdate: MobileSectionPage, // Unified mobile section page
  SchoolTestStudent,
  StudentChanceManagement,
  ImageTester,
  TesterDashboard,
  ActivityPromptManager,
  UserManagement,
  SchoolManagement,
  Unauthorized,
  AccessCode,
  Test: MobileSectionPage, // Unified mobile section page
  MobileTest: MobileSectionPage, // Unified mobile section page
  SchoolTest: SchoolSectionPage, // Unified school section page
  ActivitySyncStatus: MobileSectionPage, // Unified mobile section page
  ProjectorActivities: ProjectorActivitiesPage,
  Activities: ActivitiesPage,
  GraphActivity: GraphActivityPage,
  OfflineActivity: OfflineActivityPage,
  TeacherActivityAssignment: TeacherActivityAssignmentPage,
};

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const NavWrapper = () => {
    const location = useLocation();
    if (location.pathname === ROUTES.LOGIN || !isAuthenticated) return null;
    return <Navbar isAuthenticated={isAuthenticated} role={role} />;
  };

  const onLoginSuccess = async (newRole) => {
    console.log('Login success callback called with role:', newRole);
    setIsLoading(true);
    setIsAuthenticated(true);
    setRole(newRole);

    try {
      const session = await authService.verifySession();
      if (session.success) {
        console.log('Authentication verified after login');
        setIsAuthenticated(true);
        setRole(newRole);
      } else {
        console.warn('Authentication verification failed after login');
        setIsAuthenticated(false);
        setRole(null);
      }
    } catch (error) {
      console.error('Error verifying session after login:', error);
      setIsAuthenticated(false);
      setRole(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      console.log('Checking authentication state...');
      setIsLoading(true);

      try {
        const session = await authService.verifySession();
        console.log('Session check result:', session);

        if (session.success) {
          console.log('User is authenticated, fetching role...');
          setIsAuthenticated(true);

          try {
            const userRole = await authService.getUserRole();
            console.log('User role fetched:', userRole);
            setRole(userRole);
          } catch (roleError) {
            console.error('Error fetching user role:', roleError);
            setRole(null);
            setIsAuthenticated(false);
          }
        } else {
          console.log('No active session found');
          setIsAuthenticated(false);
          setRole(null);
        }
      } catch (error) {
        console.error('Error during authentication check:', error);
        setIsAuthenticated(false);
        setRole(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  return (
    <UserProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <NavWrapper />
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Suspense fallback={<div style={{ color: 'white', textAlign: 'center', paddingTop: '50px' }}>Loading...</div>}>
          <Routes>
            {ROUTE_CONFIG.map((route) => {
              const Component = COMPONENT_MAP[route.component];
              if (!Component) {
                console.error(`Component ${route.component} not found in map`);
                return null;
              }

              if (route.isProtected) {
                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <RoleProtectedRoute
                        isAuthenticated={isAuthenticated}
                        role={role}
                        allowed={route.allowedRoles}
                      >
                        <Component />
                      </RoleProtectedRoute>
                    }
                  />
                );
              }

              return (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    route.path === ROUTES.LOGIN ? (
                      <Component onLoginSuccess={onLoginSuccess} />
                    ) : (
                      <Component />
                    )
                  }
                />
              );
            })}
            <Route path="*" element={
              <Navigate to={
                !isAuthenticated
                  ? ROUTES.LOGIN
                  : role === ROLES.ADMIN
                    ? ROUTES.ADMIN_DASHBOARD
                    : role === ROLES.CONTENT_CREATOR ? ROUTES.DASHBOARD : ROUTES.UNAUTHORIZED
              } />
            } />
          </Routes>
        </Suspense>
      )}
    </Router>
    </UserProvider>
  );
}
